/**
 * Context-Aware Suggestions
 * Shows relevant actions based on current page
 */
import React from 'react';
import styled from 'styled-components';
import { Button, Tag } from 'antd';
import { useAIAgent } from '../../context/AIAgentContext';

const SuggestionsContainer = styled.div`
  padding: 12px;
  background: var(--color-info-light);
  border-radius: 8px;
  margin-bottom: 16px;
`;

const SuggestionButton = styled(Button)`
  margin: 4px;
  text-align: left;
`;

const ContextTag = styled(Tag)`
  margin-bottom: 8px;
`;

interface Suggestion {
  text: string;
  action: string;
  icon?: React.ReactNode;
}

export const ContextSuggestions: React.FC = () => {
  const { currentContext, processRequest } = useAIAgent();
  
  const getSuggestions = (): Suggestion[] => {
    const { pageType, pageData } = currentContext;
    
    switch (pageType) {
      case 'analytics-dashboard':
        return [
          {
            text: 'Add a widget to this dashboard',
            action: 'add widget to current dashboard',
          },
          {
            text: 'Export dashboard data',
            action: 'export this dashboard data as CSV',
          },
          {
            text: 'Create similar dashboard',
            action: 'create a dashboard like this one',
          },
        ];
        
      case 'members':
        return [
          {
            text: 'Export active members',
            action: 'export list of active members',
          },
          {
            text: 'Track member engagement',
            action: 'start tracking member engagement weekly',
          },
          {
            text: 'Member demographics dashboard',
            action: 'create dashboard showing member demographics',
          },
        ];
        
      case 'events':
        return [
          {
            text: 'Track event attendance',
            action: 'track attendance for this event',
          },
          {
            text: 'Event analytics dashboard',
            action: 'create event attendance dashboard',
          },
        ];
        
      case 'dashboard':
        return [
          {
            text: 'Create attendance dashboard',
            action: 'create dashboard for attendance tracking',
          },
          {
            text: 'View member analytics',
            action: 'take me to member analytics',
          },
        ];
        
      default:
        return [
          {
            text: 'Create a dashboard',
            action: 'create a new analytics dashboard',
          },
          {
            text: 'Navigate somewhere',
            action: 'show me the members page',
          },
        ];
    }
  };
  
  const handleSuggestion = async (action: string) => {
    await processRequest(action);
  };
  
  const suggestions = getSuggestions();
  const { pageType } = currentContext;
  
  if (!pageType) {
    return null; // Don't render if context not ready
  }
  
  return (
    <SuggestionsContainer>
      <div>
        <ContextTag color="blue">
          Currently on: {pageType.replace('-', ' ')}
        </ContextTag>
      </div>
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        Try these actions:
      </div>
      {suggestions.map((suggestion, index) => (
        <SuggestionButton
          key={index}
          size="small"
          onClick={() => handleSuggestion(suggestion.action)}
        >
          {suggestion.text}
        </SuggestionButton>
      ))}
    </SuggestionsContainer>
  );
};